import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import PaymentFlow from './services/paymentFlow';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import PaymentValidationModal from './components/PaymentValidationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lucide React Icons
import { FileText, Upload, Download, Printer, Mail, Check } from 'lucide-react';

// UI Components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = '' }) => (
  <h2 className={`text-xl font-semibold ${className}`}>{children}</h2>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 pt-0 ${className}`}>{children}</div>
);

const Button = ({ children, onClick, disabled, className = '', type = 'button', variant = 'default' }) => {
  const baseClasses = 'px-6 py-3 rounded-lg font-semibold transition-all';
  const variantClasses = variant === 'outline' 
    ? 'border-2 border-slate-300 hover:bg-slate-50' 
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 mb-2">
    {children}
  </label>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const Textarea = ({ ...props }) => (
  <textarea
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const MMKGPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // Coordinated loading state
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [pricingInfo, setPricingInfo] = useState(null);
  
  // Report viewing states
  const [viewingReport, setViewingReport] = useState(null);
  const [viewingReportContent, setViewingReportContent] = useState(null);
  const [generatingReportType, setGeneratingReportType] = useState(null);
  const [generatedReports, setGeneratedReports] = useState({});
  
  // Payment flow states
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating marketing kit', completed: false, current: false },
    { text: 'Compiling reports', completed: false, current: false }
  ]);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  
  const [formData, setFormData] = useState({
    book_title: '',
    subtitle: '',
    author_name: '',
    author_bio: '',
    publisher_name: '',
    publisher_url: '',
    author_url: '',
    book_price: '',
    release_date: '',
    target_media: '',
    promotional_angles: '',
    author_contact_email: '',
    author_contact_phone: '',
    author_social_media: '',
    publisher_background: '',
    manuscript_file: null
  });
  
  const [availabilityLinks, setAvailabilityLinks] = useState([
    { format: 'paperback', url: '' },
    { format: 'ebook', url: '' }
  ]);
  
  const [manuscriptContent, setManuscriptContent] = useState('');
  const [bookCoverImage, setBookCoverImage] = useState(null);
  const [bookCoverPreview, setBookCoverPreview] = useState(null);
  const [authorHeadshot, setAuthorHeadshot] = useState(null);
  const [authorHeadshotPreview, setAuthorHeadshotPreview] = useState(null);
  
  // Dashboard state
  const [showDashboard, setShowDashboard] = useState(false); // Start with form, not dashboard
  const [mediaKits, setMediaKits] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [paidUses, setPaidUses] = useState(0);

  useEffect(() => {
    // Coordinated initialization - run all startup tasks in sequence
    const initializeComponent = async () => {
      try {
        setIsInitializing(true);
        
        // Step 1: Validate service access first
        await validateServiceAccess();
        
        // Step 2: Load media kits for dashboard
        await fetchMediaKits();
        
        // Step 3: Load specific report if reportId passed via navigation state
        if (location.state?.reportId) {
          await loadReport(location.state.reportId);
          setShowDashboard(false);
        }
      } catch (error) {
        toast.error('Failed to load service. Please refresh the page.');
      } finally {
        setIsInitializing(false);
      }
    };
    
    initializeComponent();
    
    // Cleanup function to reset state when component unmounts
    return () => {
      setReport(null);
      setViewingReport(null);
      setViewingReportContent(null);
      setGeneratedReports({});
      setFormData({
        book_title: '',
        subtitle: '',
        author_name: '',
        author_bio: '',
        publisher_name: '',
        publisher_url: '',
        author_url: '',
        book_price: '',
        release_date: '',
        target_media: '',
        promotional_angles: '',
        author_contact_email: '',
        author_contact_phone: '',
        author_social_media: '',
        publisher_background: '',
        manuscript_file: null
      });
    };
  }, [location.state]);
  
  const fetchMediaKits = async () => {
    try {
      const response = await axios.get(`${API}/mmkg/reports`, { withCredentials: true });
      if (response.data.success && response.data.reports) {
        setMediaKits(response.data.reports);
        
        // Calculate total spent and paid uses
        const spent = response.data.reports.reduce((sum, kit) => sum + (kit.charge_amount || 0), 0);
        const paid = response.data.reports.filter(kit => kit.charge_amount > 0).length;
        setTotalSpent(spent);
        setPaidUses(paid);
      }
    } catch (error) {
    }
  };

  const loadReport = async (reportId) => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/mmkg/reports/${reportId}`, { withCredentials: true });
      setReport(response.data);
      
      // Populate generatedReports state from database
      if (response.data.reports) {
        const loadedReports = {};
        Object.keys(response.data.reports).forEach(reportType => {
          if (response.data.reports[reportType]?.generated) {
            loadedReports[reportType] = response.data.reports[reportType].content;
          }
        });
        setGeneratedReports(loadedReports);
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    // Determine pricing based on user role
    const role = user?.role || 'free_user';
    const subscriptionStatus = user?.subscription_status || '';
    
    let pricing = {};
    if (role === 'admin' || role === 'beta_tester') {
      pricing = {
        price: 0,
        message: 'Unlimited Free Access - Included with your account',
        payment_required: false
      };
    } else if (role === 'subscriber' || ['active', 'monthly', 'annual'].includes(subscriptionStatus.toLowerCase())) {
      pricing = {
        price: 0,
        message: 'Unlimited Free Access - Included with your subscription',
        payment_required: false
      };
    } else {
      pricing = {
        price: 20.00,
        message: 'Premium Service - $20.00 per use',
        payment_required: true
      };
    }
    
    setPricingInfo(pricing);
  }, [user]);

  const validateServiceAccess = async () => {
    try {
      const verification = await PaymentFlow.verifyPaymentForService('mmkg');
      
      if (verification.access_granted) {
        if (verification.discount_info) {
          setChargeInfo(verification.discount_info);
        }
        setPaymentCompleted(true);
        return;
      }
      
      if (verification.requires_payment_validation) {
        setShowPaymentValidation(true);
        setServiceAccessDenied(true);
        return;
      }
      
      setServiceAccessDenied(true);
    } catch (error) {
      setServiceAccessDenied(true);
    }
  };

  const handlePaymentValidationSuccess = (result) => {
    const roleName = result.user_role ? result.user_role.replace('_', ' ') : 'A la Carte';
    toast.success(`Payment method added! You are now an ${roleName} user.`);
    setShowPaymentValidation(false);
    setServiceAccessDenied(false);
    setPaymentCompleted(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setFormData(prev => ({ ...prev, manuscript_file: file }));
    
    // Extract text from file
    try {
      const text = await extractTextFromFile(file);
      setManuscriptContent(text);
      toast.success('Manuscript uploaded successfully');
    } catch (error) {
      toast.error('Failed to read manuscript file');
    }
  };

  const handleBookCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setBookCoverImage(base64);
      setBookCoverPreview(base64);
      toast.success('Book cover uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read book cover image');
    };
    reader.readAsDataURL(file);
  };

  const handleAuthorHeadshotUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setAuthorHeadshot(base64);
      setAuthorHeadshotPreview(base64);
      toast.success('Author headshot uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read author headshot image');
    };
    reader.readAsDataURL(file);
  };

  const extractTextFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        
        if (file.name.endsWith('.txt')) {
          resolve(content);
        } else if (file.name.endsWith('.pdf')) {
          // For PDF, just use first part of content (simplified)
          resolve(content.substring(0, 5000));
        } else if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
          // For Word docs, use simple text extraction
          resolve(content.substring(0, 5000));
        } else {
          resolve(content.substring(0, 5000));
        }
      };
      
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!manuscriptContent) {
      toast.error('Please upload a manuscript file');
      return;
    }
    
    // Check if user needs payment method setup
    if (user.role === 'freemium' || (user.role === 'a_la_carte' && !user.payment_method)) {
      setShowPaymentValidation(true);
      return;
    }
    
    // Calculate manuscript word count for modal display
    const wordCount = manuscriptContent.trim().split(/\s+/).length;
    const manuscriptInfo = `${wordCount.toLocaleString()} words`;
    
    // Set detailed charge info with submission details for confirmation modal
    setChargeInfo({
      service: 'mmkg',
      name: 'Manuscript Marketing Kit Generator',
      amount: 20.00,  // Premium service - $20 for a la carte
      description: 'Manuscript Marketing Kit Generator - Professional Publicity Materials',
      details: {
        book_title: formData.book_title,
        author_name: formData.author_name,
        publisher_name: formData.publisher_name,
        manuscript_info: manuscriptInfo
      }
    });
    setShowChargeModal(true);
  };

  const handleServiceChargeAuthorization = async () => {
    // User confirmed they want to proceed - close modal and submit
    setShowChargeModal(false);
    await submitForProcessing();
  };

  const submitForProcessing = async () => {
    setIsProcessing(true);
    setLoading(true);
    
    // Step 1: Analyzing
    setCurrentStep('Analyzing manuscript and marketing data...');
    setProgressSteps([
      { text: 'Analyzing manuscript', completed: false, current: true },
      { text: 'Generating marketing kit', completed: false, current: false },
      { text: 'Compiling reports', completed: false, current: false }
    ]);
    setProcessingProgress(10);
    
    try {
      setProcessingProgress(25);
      
      // Step 2: Generating
      setCurrentStep('Generating professional marketing materials...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating marketing kit', completed: false, current: true },
        { text: 'Compiling reports', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(
        `${API}/mmkg/submit`,
        {
          book_title: formData.book_title,
          subtitle: formData.subtitle || null,
          author_name: formData.author_name,
          author_bio: formData.author_bio,
          publisher_name: formData.publisher_name,
          publisher_url: formData.publisher_url || null,
          author_url: formData.author_url || null,
          book_price: parseFloat(formData.book_price),
          release_date: formData.release_date,
          availability_links: availabilityLinks.filter(link => link.url),
          target_media: formData.target_media,
          promotional_angles: formData.promotional_angles,
          author_contact_email: formData.author_contact_email,
          author_contact_phone: formData.author_contact_phone || null,
          author_social_media: formData.author_social_media || null,
          publisher_background: formData.publisher_background || null,
          book_cover_image: bookCoverImage,
          author_headshot: authorHeadshot,
          manuscript_content: manuscriptContent
        },
        { 
          withCredentials: true,
          timeout: 120000 // 2 minutes - simplified version
        }
      );
      
      // Step 3: Compiling
      setCurrentStep('Compiling marketing kit components...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating marketing kit', completed: true, current: false },
        { text: 'Compiling reports', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      if (response.data.success) {
        setReport(response.data);
        setLastTransactionId(response.data.transaction_id);
        setLastCharge(response.data.charge_amount || 0);
        
        // Complete
        setProgressSteps([
          { text: 'Analyzing manuscript', completed: true, current: false },
          { text: 'Generating marketing kit', completed: true, current: false },
          { text: 'Compiling reports', completed: true, current: false }
        ]);
        setProcessingProgress(100);
        setCurrentStep('Complete!');
        
        toast.success('Media Kit generated successfully!');
        fetchMediaKits(); // Refresh dashboard data
        
        // Refresh dashboard stats after successful service completion
        window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
        
        // Show receipt modal if charged
        if (response.data.charge_amount > 0) {
          setShowEmailReceiptModal(true);
        }
      } else {
        toast.error(response.data.error_message || 'Failed to generate media kit');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'An error occurred';
      
      // Check for API overload
      if (errorMessage.includes('overloaded') || errorMessage.includes('503')) {
        toast.error('AI service is temporarily overloaded. Please try again in a few moments.', {
          duration: 5000
        });
      } else if (errorMessage.includes('502') || errorMessage.includes('Bad Gateway')) {
        toast.error('AI service temporarily unavailable. Please try again shortly.', {
          duration: 5000
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  const handleNewReport = () => {
    setReport(null);
    setShowDashboard(true);
    setFormData({
      book_title: '',
      subtitle: '',
      author_name: '',
      author_bio: '',
      publisher_name: '',
      publisher_url: '',
      author_url: '',
      book_price: '',
      release_date: '',
      target_media: '',
      promotional_angles: '',
      author_contact_email: '',
      author_contact_phone: '',
      author_social_media: '',
      publisher_background: '',
      manuscript_file: null
    });
    setAvailabilityLinks([
      { format: 'paperback', url: '' },
      { format: 'ebook', url: '' }
    ]);
    setManuscriptContent('');
    setBookCoverImage(null);
    setBookCoverPreview(null);
    setAuthorHeadshot(null);
    setAuthorHeadshotPreview(null);
    fetchMediaKits(); // Refresh dashboard data
  };

  const printResults = () => {
    if (!report) return;
    
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Manuscript Media Kit Generator - ${report.book_title}</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.5; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .report-header { text-align: center; margin-bottom: 20px; }
              .report-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .report-header h1 { font-size: 18pt; margin: 8px 0; color: #1e40af; }
              .report-header h2 { font-size: 16pt; margin: 5px 0; }
              .book-info { margin: 15px 0; padding: 12px; background-color: #f8fafc; border: 1px solid #ddd; border-radius: 6px; }
              .book-info p { margin: 5px 0; font-size: 11pt; }
              .section { margin-bottom: 15px; padding: 12px; border-left: 4px solid; border-radius: 6px; page-break-inside: avoid; }
              .section h3 { font-size: 14pt; font-weight: bold; margin: 0 0 8px 0; }
              .section ul { margin: 8px 0; padding-left: 20px; }
              .section li { margin: 4px 0; }
              .footer { margin-top: 20px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } }
            </style>
          </head>
          <body>
            <div class="print-header">
              <div>Generated: ${timestamp}</div>
              <div>Report ID: ${report.report_id}</div>
            </div>
            
            <div class="report-header">
              <img src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" />
              <h1>Manuscript Media Kit Generator</h1>
              <h2>${report.book_title}</h2>
              ${report.subtitle ? `<p style="color: #666; font-size: 13pt;">${report.subtitle}</p>` : ''}
            </div>
            
            <div class="book-info">
              <p><strong>ISBN:</strong> ${formData.isbn}</p>
              <p><strong>Publisher:</strong> ${formData.publisher_name}</p>
              <p><strong>Price:</strong> $${formData.book_price}</p>
            </div>
            
            <div class="section" style="border-left-color: #3b82f6; background-color: #eff6ff;">
              <h3>📚 KDP Categories</h3>
              <ul>
                ${report.kdp_categories?.map(cat => `<li>${cat}</li>`).join('') || '<li>No categories</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #8b5cf6; background-color: #f5f3ff;">
              <h3>🔑 KDP Keywords</h3>
              <ul>
                ${report.kdp_keywords?.map(kw => `<li>${kw}</li>`).join('') || '<li>No keywords</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #10b981; background-color: #d1fae5;">
              <h3>📝 Book Description</h3>
              <p>${report.book_description || 'No description'}</p>
            </div>
            
            <div class="section" style="border-left-color: #f59e0b; background-color: #fef3c7;">
              <h3>👤 Author Bio (Optimized)</h3>
              <p>${report.author_bio_optimized || 'No bio'}</p>
            </div>
            
            <div class="section" style="border-left-color: #ec4899; background-color: #fce7f3;">
              <h3>📖 Back Cover Copy</h3>
              <p>${report.back_cover_copy || 'No copy'}</p>
            </div>
            
            <div class="section" style="border-left-color: #06b6d4; background-color: #cffafe;">
              <h3>🔍 Amazon Search Terms</h3>
              <ul>
                ${report.search_terms?.map(term => `<li>${term}</li>`).join('') || '<li>No terms</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #6366f1; background-color: #e0e7ff;">
              <h3>🎯 Age Range & Grade Level</h3>
              <p><strong>Age Range:</strong> ${report.age_range || 'N/A'}</p>
              <p><strong>Grade Level:</strong> ${report.grade_level || 'N/A'}</p>
            </div>
            
            <div class="section" style="border-left-color: #84cc16; background-color: #ecfccb;">
              <h3>📊 BISAC Categories</h3>
              <ul>
                ${report.bisac_categories?.map(cat => `<li>${cat}</li>`).join('') || '<li>No categories</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #ef4444; background-color: #fee2e2;">
              <h3>🎬 Marketing Copy</h3>
              <p>${report.marketing_copy || 'No copy'}</p>
            </div>
            
            <div class="footer">
              <p>Generated by Digital Ink AI - Manuscript Media Kit Generator</p>
              <p>© ${new Date().getFullYear()} Digital Ink AI. All rights reserved.</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
  };

  const exportToPDF = () => {
    if (!report) return;
    
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPos = 20;
    
    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Manuscript Media Kit Generator', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 10;
    pdf.setFontSize(14);
    pdf.text(report.book_title, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Book Info
    pdf.text(`ISBN: ${formData.isbn}`, margin, yPos);
    yPos += 6;
    pdf.text(`Publisher: ${formData.publisher_name}`, margin, yPos);
    yPos += 6;
    pdf.text(`Price: $${formData.book_price}`, margin, yPos);
    yPos += 10;
    
    // Sections
    const sections = [
      { title: 'KDP Categories', content: report.kdp_categories?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'KDP Keywords', content: report.kdp_keywords?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Book Description', content: report.book_description || 'None' },
      { title: 'Author Bio (Optimized)', content: report.author_bio_optimized || 'None' },
      { title: 'Back Cover Copy', content: report.back_cover_copy || 'None' },
      { title: 'Amazon Search Terms', content: report.search_terms?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Age Range & Grade Level', content: `Age Range: ${report.age_range}\nGrade Level: ${report.grade_level}` },
      { title: 'BISAC Categories', content: report.bisac_categories?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Marketing Copy', content: report.marketing_copy || 'None' }
    ];
    
    sections.forEach((section, index) => {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(section.title, margin, yPos);
      yPos += 7;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(section.content, maxWidth);
      lines.forEach(line => {
        if (yPos > 270) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      
      yPos += 5;
    });
    
    pdf.save(`MMKG-${report.book_title.replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.pdf`);
    toast.success('PDF exported successfully!');
  };



  // Handle viewing/generating individual reports
  const handleViewReport = async (reportType, reportLabel) => {
    // Check if already generated
    if (generatedReports[reportType]) {
      setViewingReport(reportType);
      setViewingReportContent(generatedReports[reportType]);
      // Scroll to anchor point with offset
      setTimeout(() => {
        const anchor = document.getElementById('report-anchor');
        if (anchor) {
          const yOffset = -80; // 80px offset from top
          const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    // Generate the report
    try {
      setGeneratingReportType(reportType);
      // Don't use isProcessing - we'll show an inline spinner instead
      
      const response = await axios.post(
        `${API}/mmkg/generate-report/${report.id}?report_type=${reportType}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const content = response.data.content;
        setGeneratedReports(prev => ({ ...prev, [reportType]: content }));
        setViewingReport(reportType);
        setViewingReportContent(content);
        toast.success(`${reportLabel} generated successfully!`);
        
        // Scroll to anchor point with offset
        setTimeout(() => {
          const anchor = document.getElementById('report-anchor');
          if (anchor) {
            const yOffset = -80; // 80px offset from top
            const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      toast.error(`Failed to generate ${reportLabel}`);
    } finally {
      setGeneratingReportType(null);
    }
  };

  const generatePrintHTML = () => {
    if (!viewingReportContent || !report) return null;
    
    // Build report title
    const reportTitles = {
      'marketing_analysis': 'Marketing Analysis',
      'press_release': 'Press Release',
      'author_bio': 'Author Bio',
      'one_sheet': 'Media One-Sheet',
      'interview_questions': 'Interview Questions',
      'book_description_short': 'Book Description (Short)',
      'book_description_long': 'Book Description (Long)',
      'social_media_posts': 'Social Media Posts',
      'talking_points': 'Key Talking Points',
      'review_quotes': 'Review Quotes Template'
    };
    
    const reportTitle = reportTitles[viewingReport] || 'Report';
    
    // Helper function to clean and escape text
    const cleanText = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };
    
    // Build content based on report type
    let contentHTML = '';
    
    if (viewingReport === 'marketing_analysis') {
      // Add book cover if available
      if (report.book_cover) {
        contentHTML += `<div class="print-section" style="text-align: center;"><img src="${report.book_cover}" alt="Book Cover" style="max-width: 200px; height: auto; border: 1px solid #ddd;" /></div>`;
      }
      if (viewingReportContent.book_overview) contentHTML += `<div class="print-section"><h3>Book Overview</h3><p>${cleanText(viewingReportContent.book_overview)}</p></div>`;
      if (viewingReportContent.narrative_hook) contentHTML += `<div class="print-section"><h3>Narrative Hook</h3><p><em>${cleanText(viewingReportContent.narrative_hook)}</em></p></div>`;
      if (viewingReportContent.market_positioning) contentHTML += `<div class="print-section"><h3>Market Positioning</h3><p>${cleanText(viewingReportContent.market_positioning)}</p></div>`;
      if (viewingReportContent.target_audience) contentHTML += `<div class="print-section"><h3>Target Audience</h3><p>${cleanText(viewingReportContent.target_audience)}</p></div>`;
      if (viewingReportContent.why_this_will_sell) contentHTML += `<div class="print-section"><h3>Why This Will Sell</h3><p>${cleanText(viewingReportContent.why_this_will_sell)}</p></div>`;
      if (viewingReportContent.author_bio_summary) contentHTML += `<div class="print-section"><h3>Author Bio Summary</h3><p>${cleanText(viewingReportContent.author_bio_summary)}</p></div>`;
    } else if (viewingReport === 'press_release') {
      // Press release with book cover
      let pressContent = '';
      if (report.book_cover) {
        pressContent += `<div style="float: right; margin: 0 0 10pt 10pt;"><img src="${report.book_cover}" alt="Book Cover" style="max-width: 150px; height: auto; border: 1px solid #ddd;" /></div>`;
      }
      pressContent += `<h2>${cleanText(viewingReportContent.title || '')}</h2><p>${cleanText(viewingReportContent.content || '')}</p>`;
      contentHTML = pressContent;
    } else if (viewingReport === 'author_bio') {
      // Author bio with headshot
      if (report.author_headshot) {
        contentHTML += `<div class="print-section" style="text-align: center;"><img src="${report.author_headshot}" alt="Author Photo" style="max-width: 150px; height: auto; border-radius: 50%; border: 2px solid #ddd;" /></div>`;
      }
      if (viewingReportContent.short_bio) contentHTML += `<div class="print-section"><h3>Short Bio (75-100 words)</h3><p>${cleanText(viewingReportContent.short_bio)}</p></div>`;
      if (viewingReportContent.long_bio) contentHTML += `<div class="print-section"><h3>Long Bio (200-250 words)</h3><p>${cleanText(viewingReportContent.long_bio)}</p></div>`;
    } else if (viewingReport === 'one_sheet') {
      // Media One-Sheet with book cover and author photo
      contentHTML += `<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10pt; margin-bottom: 10pt;">`;
      if (report.book_cover) {
        contentHTML += `<div style="text-align: center;"><img src="${report.book_cover}" alt="Book Cover" style="max-width: 180px; height: auto; border: 1px solid #ddd;" /><p style="font-size: 8pt; margin-top: 4pt;">Book Cover</p></div>`;
      } else {
        contentHTML += `<div></div>`;
      }
      if (report.author_headshot) {
        contentHTML += `<div style="text-align: center;"><img src="${report.author_headshot}" alt="Author Photo" style="max-width: 150px; height: auto; border-radius: 50%; border: 2px solid #ddd;" /><p style="font-size: 8pt; margin-top: 4pt;">Author Photo</p></div>`;
      } else {
        contentHTML += `<div></div>`;
      }
      contentHTML += `</div>`;
      
      if (viewingReportContent.hook) contentHTML += `<div class="print-section"><h3>Hook</h3><p>${cleanText(viewingReportContent.hook)}</p></div>`;
      if (viewingReportContent.synopsis) contentHTML += `<div class="print-section"><h3>Synopsis</h3><p>${cleanText(viewingReportContent.synopsis)}</p></div>`;
      if (Array.isArray(viewingReportContent.key_themes)) {
        contentHTML += `<div class="print-section"><h3>Key Themes</h3><ul>${viewingReportContent.key_themes.map(t => `<li>${cleanText(t)}</li>`).join('')}</ul></div>`;
      }
      if (Array.isArray(viewingReportContent.target_audience)) {
        contentHTML += `<div class="print-section"><h3>Target Audience</h3><ul>${viewingReportContent.target_audience.map(a => `<li>${cleanText(a)}</li>`).join('')}</ul></div>`;
      }
    } else if (viewingReport === 'interview_questions') {
      // Interview Questions formatted by category
      if (viewingReportContent.warm_up) {
        contentHTML += `<div class="print-section"><h3>Warm-Up Questions</h3><ol>${viewingReportContent.warm_up.map(q => `<li>${cleanText(q)}</li>`).join('')}</ol></div>`;
      }
      if (viewingReportContent.deep_dive) {
        contentHTML += `<div class="print-section"><h3>Deep-Dive Questions</h3><ol>${viewingReportContent.deep_dive.map(q => `<li>${cleanText(q)}</li>`).join('')}</ol></div>`;
      }
      if (viewingReportContent.engagement) {
        contentHTML += `<div class="print-section"><h3>Audience Engagement Questions</h3><ol>${viewingReportContent.engagement.map(q => `<li>${cleanText(q)}</li>`).join('')}</ol></div>`;
      }
      if (viewingReportContent.promotional) {
        contentHTML += `<div class="print-section"><h3>Promotional Hook Questions</h3><ol>${viewingReportContent.promotional.map(q => `<li>${cleanText(q)}</li>`).join('')}</ol></div>`;
      }
    } else if (viewingReport === 'book_description_short') {
      contentHTML += `<div class="print-section"><p style="font-style: italic; background: #f8f9fa; padding: 10pt; border-left: 3pt solid #007bff;">${cleanText(viewingReportContent.short_description)}</p></div>`;
      contentHTML += `<p style="font-size: 8pt; color: #666; margin-top: 8pt;"><em>Perfect for Amazon, social media, and quick promotional use (150 words)</em></p>`;
    } else if (viewingReport === 'book_description_long') {
      contentHTML += `<div class="print-section"><p>${cleanText(viewingReportContent.long_description)}</p></div>`;
      contentHTML += `<p style="font-size: 8pt; color: #666; margin-top: 8pt;"><em>For publisher websites, press releases, and detailed marketing materials (300 words)</em></p>`;
    } else if (viewingReport === 'social_media_posts') {
      // Social Media Posts by platform
      if (viewingReportContent.twitter) {
        contentHTML += `<div class="print-section"><h3>𝕏 Twitter/X Posts</h3>`;
        viewingReportContent.twitter.forEach((post, i) => {
          contentHTML += `<div style="background: #f0f8ff; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #1da1f2;"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.instagram) {
        contentHTML += `<div class="print-section"><h3>📷 Instagram Posts</h3>`;
        viewingReportContent.instagram.forEach((post, i) => {
          contentHTML += `<div style="background: #fff5f8; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #e1306c;"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.facebook) {
        contentHTML += `<div class="print-section"><h3>👍 Facebook Posts</h3>`;
        viewingReportContent.facebook.forEach((post, i) => {
          contentHTML += `<div style="background: #f0f4ff; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #1877f2;"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
        });
        contentHTML += `</div>`;
      }
      if (viewingReportContent.linkedin) {
        contentHTML += `<div class="print-section"><h3>💼 LinkedIn Posts</h3>`;
        viewingReportContent.linkedin.forEach((post, i) => {
          contentHTML += `<div style="background: #f0f7ff; padding: 8pt; margin: 6pt 0; border-left: 3pt solid #0077b5;"><strong>Post ${i+1}:</strong><br>${cleanText(post)}</div>`;
        });
        contentHTML += `</div>`;
      }
    } else if (viewingReport === 'talking_points') {
      // Talking Points with icons
      const icons = { elevator_pitch: '🎯', origin_story: '📖', big_idea: '💡', audience_connection: '🤝', differentiator: '⭐', call_to_action: '📢' };
      Object.entries(viewingReportContent).forEach(([key, value]) => {
        const title = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const icon = icons[key] || '▪️';
        contentHTML += `<div class="print-section"><h3>${icon} ${title}</h3><p>${cleanText(value)}</p></div>`;
      });
    } else if (viewingReport === 'review_quotes') {
      // Review Quotes as testimonials
      if (viewingReportContent.quotes && Array.isArray(viewingReportContent.quotes)) {
        viewingReportContent.quotes.forEach((quote, i) => {
          contentHTML += `<div class="print-section" style="background: #fffbf0; padding: 10pt; border-left: 3pt solid #ffc107;">`;
          contentHTML += `<p style="font-style: italic; margin-bottom: 6pt;">"${cleanText(quote.text)}"</p>`;
          contentHTML += `<p style="font-size: 9pt; color: #666;">${cleanText(quote.attribution)}</p>`;
          if (quote.rating) contentHTML += `<p style="font-size: 9pt; font-weight: bold; color: #f39c12;">${cleanText(quote.rating)}</p>`;
          contentHTML += `</div>`;
        });
      }
    } else {
      // Fallback for any undefined report types
      contentHTML += `<div class="print-section"><p><em>Report content preview not available. Please view on-screen for full details.</em></p></div>`;
    }
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <meta charset="UTF-8">
        <style>
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          @page { margin: 0.4in; size: letter; }
          .header { border: 2px solid #000; padding: 10px; margin-bottom: 12px; }
          .logo-img { text-align: center; margin-bottom: 8px; }
          .logo-img img { height: 45px; max-width: 100%; }
          .report-title { font-size: 16pt; font-weight: bold; text-align: center; border-bottom: 1px solid #000; padding-bottom: 6px; margin: 8px 0; }
          .metadata { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 9pt; line-height: 1.3; }
          h2 { font-size: 14pt; font-weight: bold; margin: 8pt 0 6pt 0; }
          h3 { font-size: 12pt; font-weight: bold; margin: 6pt 0 4pt 0; }
          p { font-size: 10pt; line-height: 1.4; margin: 0 0 6pt 0; white-space: pre-wrap; }
          ul, ol { font-size: 10pt; line-height: 1.4; margin: 4pt 0; padding-left: 20pt; }
          li { margin-bottom: 3pt; }
          .print-section { border-bottom: 1px solid #ddd; padding-bottom: 6pt; margin-bottom: 8pt; page-break-inside: avoid; }
          .print-section:last-child { border-bottom: none; }
          .footer { margin-top: 12pt; padding-top: 8pt; text-align: center; font-size: 8pt; color: #666; border-top: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-img">
            <img src="https://customer-assets.emergentagent.com/job_authoring-hub/artifacts/5v7reazk_Digital_Ink_AI_Logo.jpg" alt="Digital Ink AI" />
          </div>
          <h1 class="report-title">${reportTitle}</h1>
          <div class="metadata">
            <div><strong>Book Title:</strong> ${cleanText(report.book_title || report.title || 'N/A')}</div>
            <div><strong>Author:</strong> ${cleanText(report.author_name || 'N/A')}</div>
            <div><strong>Subtitle:</strong> ${cleanText(report.subtitle || 'N/A')}</div>
            <div><strong>Publisher:</strong> ${cleanText(report.publisher_name || 'N/A')}</div>
            <div><strong>Sales Price:</strong> $${cleanText(report.book_price || report.summary?.book_price || 'Not specified')}</div>
            <div><strong>Release Date:</strong> ${cleanText(report.release_date || report.summary?.release_date || 'Not specified')}</div>
            ${report.isbn ? `<div style="grid-column: span 2;"><strong>ISBN:</strong> ${cleanText(report.isbn)}</div>` : ''}
          </div>
        </div>
        ${contentHTML}
        <div class="footer">Generated with Digital Ink AI - www.DigitalInkAI.com</div>
      </body>
      </html>
    `;
  };

  const handlePrintReport = () => {
    try {
      // Validate that we have a report to print
      if (!viewingReport || !viewingReportContent) {
        toast.error('No report selected for printing');
        return;
      }
      
      // Generate HTML
      const printHTML = generatePrintHTML();
      if (!printHTML) {
        toast.error('Unable to generate print preview');
        return;
      }
      
      // Open in new window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for content to load, then print
        setTimeout(() => {
          printWindow.print();
        }, 500);
      } else {
        toast.error('Unable to open print window. Please check popup blocker.');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate print preview');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Get or generate HTML version
      const reportKey = `${viewingReport}_html`;
      let printHTML = generatedReports[reportKey];
      
      if (!printHTML) {
        printHTML = generatePrintHTML();
        if (!printHTML) {
          toast.error('Unable to generate PDF');
          return;
        }
        
        // Save HTML version to backend
        await axios.post(
          `${API}/mmkg/save-print-html/${report.id}`,
          { 
            report_type: viewingReport,
            html_content: printHTML 
          },
          { withCredentials: true }
        );
        
        setGeneratedReports(prev => ({ ...prev, [reportKey]: printHTML }));
      }
      
      // Create hidden iframe to render HTML for PDF conversion
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.write(printHTML);
      iframeDoc.close();
      
      // Wait for content to render
      setTimeout(() => {
        // Use jsPDF to convert to PDF
        const pdf = new jsPDF('p', 'pt', 'letter');
        
        pdf.html(iframeDoc.body, {
          callback: function (doc) {
            const reportName = viewingReport.replace(/_/g, '-');
            doc.save(`${report.book_title}-${reportName}.pdf`);
            document.body.removeChild(iframe);
            toast.success('PDF exported successfully!');
          },
          x: 40,
          y: 40,
          width: 535,
          windowWidth: 800
        });
      }, 500);
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  return (
    <>
      {/* Global Initialization Loading Overlay */}
      {isInitializing && (
        <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
            <p className="text-xl font-semibold text-slate-700">Loading Service...</p>
            <p className="text-sm text-slate-500 mt-2">Preparing your workspace</p>
          </div>
        </div>
      )}
      
      {/* Print-only styles */}
      <style>{`
        /* Hide print content (rendered but invisible) */
        #print-report-content {
          visibility: hidden;
          height: 0;
          overflow: hidden;
        }
        
        @media print {
          /* Page setup */
          @page {
            margin: 0.4in;
            size: letter;
          }
          
          /* Reset body and html */
          html, body {
            margin: 0;
            padding: 0;
            height: auto;
          }
          
          /* Hide the main app content but keep React root visible */
          .min-h-screen,
          nav, header, aside,
          button, input, select, textarea,
          [class*="Card"]:not(#print-report-content),
          .print\\:hidden {
            display: none !important;
          }
          
          /* Show ONLY the print content */
          #print-report-content {
            display: block !important;
            position: static !important;
            left: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Compact typography for print */
          h1 { 
            font-size: 18pt; 
            font-weight: bold; 
            margin: 0 0 8pt 0; 
            line-height: 1.2;
            page-break-after: avoid;
          }
          h2 { 
            font-size: 14pt; 
            font-weight: bold; 
            margin: 8pt 0 6pt 0; 
            line-height: 1.2;
            page-break-after: avoid;
          }
          h3 { 
            font-size: 12pt; 
            font-weight: bold; 
            margin: 6pt 0 4pt 0; 
            line-height: 1.2;
            page-break-after: avoid;
          }
          p { 
            font-size: 10pt; 
            line-height: 1.4; 
            margin: 0 0 6pt 0; 
          }
          li { 
            font-size: 10pt; 
            line-height: 1.4; 
            margin-bottom: 3pt;
          }
          ul, ol {
            margin: 4pt 0;
            padding-left: 20pt;
          }
          
          /* Remove backgrounds and shadows */
          * {
            background: white !important;
            box-shadow: none !important;
          }
          
          /* Compact print sections */
          .print-section {
            border-bottom: 1px solid #ddd;
            padding-bottom: 6pt;
            margin-bottom: 8pt;
            page-break-inside: avoid;
          }
          
          .print-section:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }
          
          /* Compact header */
          #print-report-content > div:first-child {
            margin: 0 0 12pt 0 !important;
            padding: 10pt !important;
          }
          
          /* Footer styling - relative instead of fixed */
          .print-footer {
            margin-top: 12pt;
            padding-top: 8pt;
            text-align: center;
            font-size: 8pt;
            color: #666;
            border-top: 1px solid #ddd;
          }
          
          /* Ensure images fit */
          img {
            max-width: 100%;
            height: auto;
          }
        }
      `}</style>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-slate-100 py-8">
      {loadingReport ? (
        // Loading State
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading media kit...</p>
          </div>
        </div>
      ) : showDashboard && !report ? (
        // Dashboard View
        <div className="max-w-7xl mx-auto px-4">
          {/* Dashboard Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 mb-6 text-white">
            <div className="flex items-center justify-center mb-6">
              <span className="text-4xl mr-3">📰</span>
              <h1 className="text-3xl font-bold">Manuscript Media Kit Generator</h1>
            </div>
            
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6 text-center">
                  <div className="text-sm text-orange-100 mb-2">Total Media Kits</div>
                  <div className="text-4xl font-bold">{mediaKits.length}</div>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6 text-center">
                  <div className="text-sm text-orange-100 mb-2">Total Spent</div>
                  <div className="text-4xl font-bold">${totalSpent.toFixed(2)}</div>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6 text-center">
                  <div className="text-sm text-orange-100 mb-2">Paid Uses</div>
                  <div className="text-4xl font-bold">{paidUses}</div>
                </CardContent>
              </Card>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/transactions?service=MMKG')}
                className="bg-orange-700 hover:bg-orange-800 text-white"
              >
                View Transaction History
              </Button>
              <Button
                onClick={() => setShowDashboard(false)}
                className="bg-white text-orange-600 hover:bg-orange-50"
              >
                📰 Create New Media Kit
              </Button>
            </div>
          </div>
          
          {/* Media Kits List */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl">
                <span className="text-3xl mr-3">📰</span>
                Media Kits ({mediaKits.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mediaKits.length === 0 ? (
                <div className="text-center py-12 bg-orange-50 rounded-lg">
                  <span className="text-6xl mb-4 block">📰</span>
                  <p className="text-lg text-slate-600 mb-6">No media kits generated yet</p>
                  <Button
                    onClick={() => setShowDashboard(false)}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Generate Your First Media Kit
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {mediaKits.map((kit) => (
                    <Card key={kit.id} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg text-slate-900">
                              {kit.book_title}
                            </CardTitle>
                            {kit.subtitle && (
                              <p className="text-md text-slate-700 italic">{kit.subtitle}</p>
                            )}
                            <p className="text-sm text-slate-600 mt-1">
                              Author: {kit.author_name} • Publisher: {kit.publisher_name}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Generated: {new Date(kit.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            {kit.charge_amount > 0 ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                                ${kit.charge_amount.toFixed(2)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                FREE
                              </span>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-slate-600 mb-4">
                          <strong>{kit.reports?.length || 0} reports generated:</strong> Press Release, Media One-Sheet, Author Bios, and more
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setReport(kit);
                              setShowDashboard(false);
                            }}
                          >
                            📖 View All Reports
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-800"
                            onClick={async () => {
                              if (window.confirm('Delete this media kit? This cannot be undone.')) {
                                try {
                                  await axios.delete(`${API}/mmkg/reports/${kit.id}`, { withCredentials: true });
                                  toast.success('Media kit deleted');
                                  fetchMediaKits();
                                } catch (error) {
                                  toast.error('Failed to delete media kit');
                                }
                              }
                            }}
                          >
                            🗑️ Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : !report ? (
        // Form Page
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
          {/* Header with Logo and Navigation */}
          <header className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img 
                    src="/Digital_Ink_AI_Logo.png" 
                    alt="Digital Ink AI Logo" 
                    className="h-12 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/homepage')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    🏠 Home
                  </button>
                  <button
                    onClick={() => navigate('/user_dashboard')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    📊 Dashboard
                  </button>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📰 Manuscript Media Kit Generator
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Equip yourself with professional-grade publicity materials traditionally available only from established publishing houses. Generate comprehensive media kits including press releases, author bios (multiple formats), interview questions, talking points, and review request letters—all professionally formatted and publication-ready for journalists, bookstores, podcast hosts, reviewers, and media outlets.
              </p>
            </div>

            {/* Pricing Info */}
            {pricingInfo && (
              <div className="mb-8 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-6">
                <div className="text-center">
                  {pricingInfo.payment_required ? (
                    <div>
                      <h3 className="text-xl font-bold text-orange-900 mb-2">💎 Standard Service - ${pricingInfo.price}</h3>
                      <p className="text-orange-700">{pricingInfo.message}</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - generate as many media kits as you need!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Form */}
          // Form
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <CardTitle className="text-2xl text-slate-900">📚 Book Information</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Book Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="book_title">Book Title *</Label>
                        <Input
                          id="book_title"
                          value={formData.book_title}
                          onChange={(e) => setFormData(prev => ({ ...prev, book_title: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="subtitle">Subtitle</Label>
                        <Input
                          id="subtitle"
                          value={formData.subtitle}
                          onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="author_name">Author Name *</Label>
                        <Input
                          id="author_name"
                          value={formData.author_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="publisher_name">Publisher Name *</Label>
                        <Input
                          id="publisher_name"
                          value={formData.publisher_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, publisher_name: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="book_price">Book Price (USD) *</Label>
                        <Input
                          id="book_price"
                          type="number"
                          step="0.01"
                          value={formData.book_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, book_price: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="release_date">Release/Publication Date *</Label>
                        <Input
                          id="release_date"
                          type="date"
                          value={formData.release_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, release_date: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    {/* URLs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="publisher_url">Publisher Website</Label>
                        <Input
                          id="publisher_url"
                          type="url"
                          placeholder="https://..."
                          value={formData.publisher_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, publisher_url: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="author_url">Author Website</Label>
                        <Input
                          id="author_url"
                          type="url"
                          placeholder="https://..."
                          value={formData.author_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, author_url: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Availability Links */}
                    <div className="space-y-3">
                      <Label>Book Availability Links *</Label>
                      {availabilityLinks.map((link, index) => (
                        <div key={index} className="flex gap-3 items-end">
                          <div className="flex-1">
                            <select
                              value={link.format}
                              onChange={(e) => {
                                const newLinks = [...availabilityLinks];
                                newLinks[index].format = e.target.value;
                                setAvailabilityLinks(newLinks);
                              }}
                              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                            >
                              <option value="paperback">Paperback</option>
                              <option value="ebook">eBook</option>
                              <option value="hardcover">Hardcover</option>
                              <option value="audiobook">Audiobook</option>
                            </select>
                          </div>
                          <div className="flex-[2]">
                            <Input
                              type="url"
                              placeholder="https://amazon.com/..."
                              value={link.url}
                              onChange={(e) => {
                                const newLinks = [...availabilityLinks];
                                newLinks[index].url = e.target.value;
                                setAvailabilityLinks(newLinks);
                              }}
                              required
                            />
                          </div>
                          {availabilityLinks.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setAvailabilityLinks(availabilityLinks.filter((_, i) => i !== index))}
                              className="px-3 py-2"
                            >
                              ✕
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAvailabilityLinks([...availabilityLinks, { format: 'ebook', url: '' }])}
                        className="text-sm"
                      >
                        + Add Another Format
                      </Button>
                    </div>

                    {/* Author Bio */}
                    <div>
                      <Label htmlFor="author_bio">Author Bio *</Label>
                      <Textarea
                        id="author_bio"
                        value={formData.author_bio}
                        onChange={(e) => setFormData(prev => ({ ...prev, author_bio: e.target.value }))}
                        rows={4}
                        placeholder="Professional author biography (will be enhanced and formatted in multiple lengths)"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">This will be used to generate long, medium, and short format bios</p>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-900">Author Contact Information *</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="author_contact_email">Email *</Label>
                          <Input
                            id="author_contact_email"
                            type="email"
                            value={formData.author_contact_email}
                            onChange={(e) => setFormData(prev => ({ ...prev, author_contact_email: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="author_contact_phone">Phone</Label>
                          <Input
                            id="author_contact_phone"
                            type="tel"
                            value={formData.author_contact_phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, author_contact_phone: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="author_social_media">Social Media Handles</Label>
                        <Input
                          id="author_social_media"
                          placeholder="@twitter, @instagram, facebook.com/..."
                          value={formData.author_social_media}
                          onChange={(e) => setFormData(prev => ({ ...prev, author_social_media: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Media & Promotion */}
                    <div>
                      <Label htmlFor="target_media">Target Media Outlets *</Label>
                      <Textarea
                        id="target_media"
                        value={formData.target_media}
                        onChange={(e) => setFormData(prev => ({ ...prev, target_media: e.target.value }))}
                        rows={3}
                        placeholder="e.g., local newspapers, book podcasts, literary blogs, NPR stations..."
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="promotional_angles">Key Promotional Angles *</Label>
                      <Textarea
                        id="promotional_angles"
                        value={formData.promotional_angles}
                        onChange={(e) => setFormData(prev => ({ ...prev, promotional_angles: e.target.value }))}
                        rows={3}
                        placeholder="e.g., unique author story, timely themes, expert credentials, local connection..."
                        required
                      />
                    </div>

                    {/* Publisher Background */}
                    <div>
                      <Label htmlFor="publisher_background">Publisher Background (Optional)</Label>
                      <Textarea
                        id="publisher_background"
                        value={formData.publisher_background}
                        onChange={(e) => setFormData(prev => ({ ...prev, publisher_background: e.target.value }))}
                        rows={3}
                        placeholder="Information about your publishing house (mission, notable titles, etc.)"
                      />
                    </div>

                    {/* Image Uploads */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <Label htmlFor="book_cover">Book Cover Image</Label>
                        <Input
                          id="book_cover"
                          type="file"
                          accept="image/*"
                          onChange={handleBookCoverUpload}
                        />
                        {bookCoverPreview && (
                          <div className="mt-3">
                            <img src={bookCoverPreview} alt="Book Cover Preview" className="max-w-[150px] rounded shadow-md" />
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Will appear in press release, one-sheet, and book summary</p>
                      </div>
                      <div>
                        <Label htmlFor="author_headshot">Author Headshot</Label>
                        <Input
                          id="author_headshot"
                          type="file"
                          accept="image/*"
                          onChange={handleAuthorHeadshotUpload}
                        />
                        {authorHeadshotPreview && (
                          <div className="mt-3">
                            <img src={authorHeadshotPreview} alt="Author Headshot Preview" className="max-w-[150px] rounded shadow-md" />
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Will appear in press release, one-sheet, and author bios</p>
                      </div>
                    </div>

                    {/* Manuscript Upload */}
                    <div>
                      <Label htmlFor="manuscript">Manuscript Upload *</Label>
                      <Input
                        id="manuscript"
                        type="file"
                        accept=".txt,.pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        required
                      />
                      {formData.manuscript_file && (
                        <p className="text-sm text-green-600 mt-2">
                          ✓ {formData.manuscript_file.name} uploaded
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={loading || !manuscriptContent}
                        className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Generating Media Kit...' : 'Generate Media Kit with Digital Ink AI'}
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Professional Press Release</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Media One-Sheet</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Author Bios (3 formats: Long, Medium, Short)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Book Summary</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Author Talking Points</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Interview Question Packet</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Review Request Letters (3 versions)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Publisher Background</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </main>
        </div>
      ) : report ? (
        // Results Display - When Report Exists
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
            {/* Sticky Header with Logo and Navigation - Matching BVADM */}
            <header className="bg-white shadow-sm border-b print:hidden sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Report Metadata - Matching BVADM Style */}
                <div className="border-b pb-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Manuscript Media Kit</h2>
                      <p className="text-slate-600 mt-1">Professional Media Kit for {report.book_title}</p>
                      {formData.publisher_name && (
                        <p className="text-slate-600 text-sm">by {formData.publisher_name}</p>
                      )}
                    </div>
                    <div className="flex gap-3 print:hidden">
                      <button onClick={handleNewReport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">✨ New Analysis</button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-slate-500">Book Title:</span>
                      <p className="font-semibold text-slate-900">{report.book_title || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Subtitle:</span>
                      <p className="font-semibold text-slate-900">{report.subtitle || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Author:</span>
                      <p className="font-semibold text-slate-900">{report.author_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Publisher:</span>
                      <p className="font-semibold text-slate-900">{report.publisher_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Book Price:</span>
                      <p className="font-semibold text-slate-900">${report.book_price || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Release Date:</span>
                      <p className="font-semibold text-slate-900">{report.release_date || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service:</span>
                      <p className="font-semibold text-slate-900">MMKG</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Generated:</span>
                      <p className="font-semibold text-slate-900">
                        {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>

            {/* MMKG Multi-Report Display */}
            <div className="space-y-6">
              
              {/* Media Kit Overview - Right after metadata */}
              {report.summary && (
                <Card className="border-l-4 border-orange-500">
                  <CardHeader>
                    <CardTitle className="text-orange-900">📋 Media Kit Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Release Date:</span>
                        <p className="font-semibold">{report.release_date || report.summary.release_date}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Book Price:</span>
                        <p className="font-semibold">${report.book_price || report.summary.book_price}</p>
                      </div>
                    </div>
                    {report.summary.availability_links?.length > 0 && (
                      <div>
                        <span className="text-slate-500">Available Formats:</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {report.summary.availability_links.map((link, idx) => (
                            <a 
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-orange-100 text-orange-900 rounded-full text-xs hover:bg-orange-200"
                            >
                              {link.format}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="text-slate-500">Target Media:</span>
                      <p className="text-sm text-slate-700">{report.summary.target_media}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Promotional Angles:</span>
                      <p className="text-sm text-slate-700">{report.summary.promotional_angles}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Generated Reports List - Right after overview */}
              <Card className="border-l-4 border-blue-500">
                <CardHeader>
                  <CardTitle className="text-blue-900">📰 Generated Publicity Materials</CardTitle>
                  <p className="text-sm text-slate-600 mt-2">Click any report name to generate it on-demand. Once generated, you can print or export to PDF.</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {/* Report 1: Marketing Analysis - Already Generated */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📊</span>
                        <div className="flex-1">
                          <div className="font-bold text-slate-900 flex items-center gap-2">
                            <span className="text-green-600">✓</span>
                            Marketing Analysis
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Core analysis report (already generated)</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setViewingReport('marketing_analysis');
                          setViewingReportContent(report.summary?.marketing_analysis);
                          setTimeout(() => {
                            const anchor = document.getElementById('report-anchor');
                            if (anchor) {
                              const yOffset = -80; // 80px offset from top
                              const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }, 100);
                        }}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        📖 View Report
                      </button>
                    </div>

                    {/* Report 2: Press Release */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.press_release ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📰</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.press_release ? 'font-bold' : ''}`}>
                            {generatedReports.press_release && <span className="text-green-600">✓</span>}
                            <span>Press Release</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.press_release ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('press_release', 'Press Release')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType === 'press_release' ? '⚙️ Generating...' : generatedReports.press_release ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 3: Media One-Sheet */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.one_sheet ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📄</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.one_sheet ? 'font-bold' : ''}`}>
                            {generatedReports.one_sheet && <span className="text-green-600">✓</span>}
                            <span>Media One-Sheet</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.one_sheet ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('one_sheet', 'Media One-Sheet')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.one_sheet ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 4: Author Bio */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.author_bio ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">👤</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.author_bio ? 'font-bold' : ''}`}>
                            {generatedReports.author_bio && <span className="text-green-600">✓</span>}
                            <span>Author Bio (Short & Long)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.author_bio ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('author_bio', 'Author Bio')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.author_bio ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 3: Media One-Sheet */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.one_sheet ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📄</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.one_sheet ? 'font-bold' : ''}`}>
                            {generatedReports.one_sheet && <span className="text-green-600">✓</span>}
                            <span>Media One-Sheet</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.one_sheet ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('one_sheet', 'Media One-Sheet')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.one_sheet ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 4: Author Bio */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.author_bio ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">👤</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.author_bio ? 'font-bold' : ''}`}>
                            {generatedReports.author_bio && <span className="text-green-600">✓</span>}
                            <span>Author Bio (Short & Long)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.author_bio ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('author_bio', 'Author Bio')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.author_bio ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 5: Interview Questions */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.interview_questions ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">🎤</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.interview_questions ? 'font-bold' : ''}`}>
                            {generatedReports.interview_questions && <span className="text-green-600">✓</span>}
                            <span>Interview Questions (10-15)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.interview_questions ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('interview_questions', 'Interview Questions')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.interview_questions ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 6: Book Description (Short) */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.book_description_short ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📝</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.book_description_short ? 'font-bold' : ''}`}>
                            {generatedReports.book_description_short && <span className="text-green-600">✓</span>}
                            <span>Book Description (Short - 150 words)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.book_description_short ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('book_description_short', 'Book Description (Short)')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.book_description_short ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 7: Book Description (Long) */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.book_description_long ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📖</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.book_description_long ? 'font-bold' : ''}`}>
                            {generatedReports.book_description_long && <span className="text-green-600">✓</span>}
                            <span>Book Description (Long - 300 words)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.book_description_long ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('book_description_long', 'Book Description (Long)')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.book_description_long ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 8: Social Media Posts */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.social_media_posts ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">📱</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.social_media_posts ? 'font-bold' : ''}`}>
                            {generatedReports.social_media_posts && <span className="text-green-600">✓</span>}
                            <span>Social Media Posts (10 ready-to-use)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.social_media_posts ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('social_media_posts', 'Social Media Posts')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.social_media_posts ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 9: Talking Points */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.talking_points ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">💬</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.talking_points ? 'font-bold' : ''}`}>
                            {generatedReports.talking_points && <span className="text-green-600">✓</span>}
                            <span>Key Talking Points (For Media Interviews)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.talking_points ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('talking_points', 'Key Talking Points')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.talking_points ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>

                    {/* Report 10: Review Quotes */}
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      generatedReports.review_quotes ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-2xl">⭐</span>
                        <div className="flex-1">
                          <div className={`flex items-center gap-2 ${generatedReports.review_quotes ? 'font-bold' : ''}`}>
                            {generatedReports.review_quotes && <span className="text-green-600">✓</span>}
                            <span>Review Quotes Template (For Testimonials)</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {generatedReports.review_quotes ? 'Report ready' : 'Click View Report to generate'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewReport('review_quotes', 'Review Quotes Template')}
                        disabled={generatingReportType !== null}
                        className={`px-4 py-2 text-sm rounded ${
                          generatingReportType !== null
                            ? 'bg-slate-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {generatingReportType !== null ? '⚙️ Generating...' : generatedReports.review_quotes ? '📖 View Report' : '📥 Get Report'}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Horizontal line anchor for autoscrolling */}
              <div id="report-anchor" className="border-t-2 border-slate-300 my-6"></div>

              {/* Report Generation Modal */}
              {generatingReportType && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-8 shadow-2xl max-w-md w-full mx-4">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600"></div>
                      <p className="text-xl font-bold text-slate-800">Report Generating</p>
                      <p className="text-sm text-slate-500 text-center">This may take 5-15 seconds</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Report Viewer Section - Shows selected report (but not marketing_analysis as it has its own card) */}
              {viewingReport && viewingReportContent && viewingReport !== 'marketing_analysis' && (
                <Card id="report-viewer" className="border-l-4 border-purple-500 bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">📄</span>
                        <CardTitle className="text-white">
                          {viewingReport === 'marketing_analysis' && 'Marketing Analysis'}
                          {viewingReport === 'press_release' && 'Press Release'}
                          {viewingReport === 'author_bio' && 'Author Bio'}
                          {viewingReport === 'one_sheet' && 'Media One-Sheet'}
                          {viewingReport === 'interview_questions' && 'Interview Questions'}
                          {viewingReport === 'book_description_short' && 'Book Description (Short)'}
                          {viewingReport === 'book_description_long' && 'Book Description (Long)'}
                          {viewingReport === 'social_media_posts' && 'Social Media Posts'}
                          {viewingReport === 'talking_points' && 'Key Talking Points'}
                          {viewingReport === 'review_quotes' && 'Review Quotes Template'}
                        </CardTitle>
                      </div>
                      <button
                        onClick={() => {
                          setViewingReport(null);
                          setViewingReportContent(null);
                        }}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm"
                      >
                        ✕ Close
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="prose max-w-none p-6">
                    {/* Render report content based on type */}
                    {viewingReport === 'marketing_analysis' && (
                      <div className="space-y-4">
                        {viewingReportContent.narrative_hook && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Narrative Hook</h3>
                            <p className="text-slate-700">{viewingReportContent.narrative_hook}</p>
                          </div>
                        )}
                        {viewingReportContent.target_audience && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Target Audience</h3>
                            <p className="text-slate-700">{viewingReportContent.target_audience}</p>
                          </div>
                        )}
                        {viewingReportContent.key_themes && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Key Themes</h3>
                            <p className="text-slate-700">{viewingReportContent.key_themes}</p>
                          </div>
                        )}
                        {viewingReportContent.comparable_titles && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Comparable Titles</h3>
                            <p className="text-slate-700">{viewingReportContent.comparable_titles}</p>
                          </div>
                        )}
                        {viewingReportContent.unique_selling_points && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Unique Selling Points</h3>
                            <p className="text-slate-700">{viewingReportContent.unique_selling_points}</p>
                          </div>
                        )}
                        {viewingReportContent.media_angles && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Media Angles</h3>
                            <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.media_angles}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReport === 'press_release' && viewingReportContent.title && (
                      <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-purple-900">{viewingReportContent.title}</h2>
                        <div className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.content}</div>
                      </div>
                    )}
                    
                    {viewingReport === 'author_bio' && (
                      <div className="space-y-6">
                        {viewingReportContent.short_bio && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Short Bio (75-100 words)</h3>
                            <p className="text-slate-700">{viewingReportContent.short_bio}</p>
                          </div>
                        )}
                        {viewingReportContent.long_bio && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Long Bio (200-250 words)</h3>
                            <p className="text-slate-700">{viewingReportContent.long_bio}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReport === 'one_sheet' && (
                      <div className="space-y-6">
                        {viewingReportContent.quick_facts && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Quick Facts</h3>
                            <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.quick_facts}</p>
                          </div>
                        )}
                        {viewingReportContent.hook && (
                          <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                            <h3 className="text-lg font-bold text-blue-900">Hook</h3>
                            <p className="text-slate-700">{viewingReportContent.hook}</p>
                          </div>
                        )}
                        {viewingReportContent.synopsis && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Synopsis</h3>
                            <p className="text-slate-700 whitespace-pre-wrap">{viewingReportContent.synopsis}</p>
                          </div>
                        )}
                        {viewingReportContent.key_themes && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Key Themes</h3>
                            {Array.isArray(viewingReportContent.key_themes) ? (
                              <ul className="list-disc list-inside space-y-1 mt-2">
                                {viewingReportContent.key_themes.map((theme, i) => (
                                  <li key={i} className="text-slate-700">{theme}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-slate-700">{viewingReportContent.key_themes}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.target_audience && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Target Audience</h3>
                            {Array.isArray(viewingReportContent.target_audience) ? (
                              <ul className="list-disc list-inside space-y-1 mt-2">
                                {viewingReportContent.target_audience.map((audience, i) => (
                                  <li key={i} className="text-slate-700">{audience}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-slate-700">{viewingReportContent.target_audience}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.author_credentials && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Author Credentials</h3>
                            {Array.isArray(viewingReportContent.author_credentials) ? (
                              <ul className="list-disc list-inside space-y-1 mt-2">
                                {viewingReportContent.author_credentials.map((cred, i) => (
                                  <li key={i} className="text-slate-700">{cred}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-slate-700">{viewingReportContent.author_credentials}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.comparable_titles && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Comparable Titles</h3>
                            {Array.isArray(viewingReportContent.comparable_titles) ? (
                              <div className="space-y-3 mt-2">
                                {viewingReportContent.comparable_titles.map((comp, i) => (
                                  <div key={i} className="bg-purple-50 p-3 rounded">
                                    <p className="font-semibold text-purple-900">
                                      {comp.title} by {comp.author}
                                    </p>
                                    <p className="text-sm text-slate-700 mt-1">{comp.why}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-700">{viewingReportContent.comparable_titles}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReport === 'interview_questions' && (
                      <div className="space-y-6">
                        {viewingReportContent.warm_up && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Warm-Up Questions</h3>
                            <ul className="list-decimal list-inside space-y-2 mt-2">
                              {viewingReportContent.warm_up.map((q, i) => (
                                <li key={i} className="text-slate-700">{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {viewingReportContent.deep_dive && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Deep-Dive Questions</h3>
                            <ul className="list-decimal list-inside space-y-2 mt-2">
                              {viewingReportContent.deep_dive.map((q, i) => (
                                <li key={i} className="text-slate-700">{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {viewingReportContent.engagement && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Audience Engagement</h3>
                            <ul className="list-decimal list-inside space-y-2 mt-2">
                              {viewingReportContent.engagement.map((q, i) => (
                                <li key={i} className="text-slate-700">{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {viewingReportContent.promotional && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900">Promotional Hooks</h3>
                            <ul className="list-decimal list-inside space-y-2 mt-2">
                              {viewingReportContent.promotional.map((q, i) => (
                                <li key={i} className="text-slate-700">{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReport === 'book_description_short' && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded border border-blue-200">
                          <p className="text-slate-700 leading-relaxed">{viewingReportContent.short_description}</p>
                        </div>
                        <p className="text-xs text-slate-500">Perfect for Amazon, social media, and quick promotional use</p>
                      </div>
                    )}
                    
                    {viewingReport === 'book_description_long' && (
                      <div className="space-y-4">
                        <div className="bg-purple-50 p-4 rounded border border-purple-200">
                          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{viewingReportContent.long_description}</p>
                        </div>
                        <p className="text-xs text-slate-500">Perfect for publisher websites, press releases, and detailed marketing materials</p>
                      </div>
                    )}
                    
                    {viewingReport === 'social_media_posts' && (
                      <div className="space-y-6">
                        {viewingReportContent.twitter && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                              <span className="text-blue-500">𝕏</span> Twitter/X Posts
                            </h3>
                            <div className="space-y-3 mt-2">
                              {viewingReportContent.twitter.map((post, i) => (
                                <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                                  <p className="text-slate-700 text-sm">{post}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingReportContent.instagram && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                              <span className="text-pink-500">📷</span> Instagram Posts
                            </h3>
                            <div className="space-y-3 mt-2">
                              {viewingReportContent.instagram.map((post, i) => (
                                <div key={i} className="bg-pink-50 p-3 rounded border-l-4 border-pink-500">
                                  <p className="text-slate-700 text-sm">{post}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingReportContent.facebook && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                              <span className="text-blue-700">👍</span> Facebook Posts
                            </h3>
                            <div className="space-y-3 mt-2">
                              {viewingReportContent.facebook.map((post, i) => (
                                <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-700">
                                  <p className="text-slate-700 text-sm">{post}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingReportContent.linkedin && (
                          <div>
                            <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                              <span className="text-blue-600">💼</span> LinkedIn Posts
                            </h3>
                            <div className="space-y-3 mt-2">
                              {viewingReportContent.linkedin.map((post, i) => (
                                <div key={i} className="bg-blue-50 p-3 rounded border-l-4 border-blue-600">
                                  <p className="text-slate-700 text-sm">{post}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {viewingReport === 'talking_points' && (
                      <div className="space-y-4">
                        {Object.entries(viewingReportContent).map(([key, value]) => (
                          <div key={key} className="bg-gradient-to-r from-purple-50 to-white p-4 rounded border-l-4 border-purple-500">
                            <h4 className="font-bold text-purple-900 capitalize mb-2">{key.replace(/_/g, ' ')}</h4>
                            <p className="text-slate-700">{value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {viewingReport === 'review_quotes' && viewingReportContent.quotes && (
                      <div className="space-y-4">
                        {viewingReportContent.quotes.map((quote, i) => (
                          <div key={i} className="bg-gradient-to-r from-amber-50 to-white p-4 rounded border-l-4 border-amber-500">
                            <p className="text-slate-700 italic mb-2">"{quote.text}"</p>
                            <p className="text-sm text-slate-600">{quote.attribution}</p>
                            {quote.rating && (
                              <p className="text-sm text-amber-600 font-semibold mt-1">{quote.rating}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Fallback for other report types */}
                    {!['marketing_analysis', 'press_release', 'author_bio', 'one_sheet', 'interview_questions', 'book_description_short', 'book_description_long', 'social_media_posts', 'talking_points', 'review_quotes'].includes(viewingReport) && (
                      <div className="text-slate-700 whitespace-pre-wrap">
                        {JSON.stringify(viewingReportContent, null, 2)}
                      </div>
                    )}
                  </CardContent>
                  <div className="px-6 pb-6 flex gap-3 print:hidden">
                    <button
                      onClick={handlePrintReport}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      🖨️ Print Report
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700"
                    >
                      📄 Export PDF
                    </button>
                  </div>
                </Card>
              )}

              {/* Print-Only Content - Hidden on screen, visible when printing */}
              {viewingReport && viewingReportContent && (
                <div id="print-report-content" className="print-only-content">
                  {/* Print Header with Branding and Full Metadata */}
                  <div style={{ border: '2px solid #000', padding: '10px', marginBottom: '12px', backgroundColor: '#fff' }}>
                    {/* Logo */}
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_authoring-hub/artifacts/5v7reazk_Digital_Ink_AI_Logo.jpg" 
                        alt="Digital Ink AI" 
                        style={{ height: '45px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                    
                    {/* Report Title */}
                    <h1 style={{ margin: '0 0 8px 0', fontSize: '16pt', fontWeight: 'bold', color: '#000', textAlign: 'center', borderBottom: '1px solid #000', paddingBottom: '6px', lineHeight: '1.2' }}>
                      {viewingReport === 'marketing_analysis' && 'Marketing Analysis'}
                      {viewingReport === 'press_release' && 'Press Release'}
                      {viewingReport === 'author_bio' && 'Author Bio'}
                      {viewingReport === 'one_sheet' && 'Media One-Sheet'}
                      {viewingReport === 'interview_questions' && 'Interview Questions'}
                      {viewingReport === 'book_description_short' && 'Book Description (Short)'}
                      {viewingReport === 'book_description_long' && 'Book Description (Long)'}
                      {viewingReport === 'social_media_posts' && 'Social Media Posts'}
                      {viewingReport === 'talking_points' && 'Key Talking Points'}
                      {viewingReport === 'review_quotes' && 'Review Quotes Template'}
                    </h1>
                    
                    {/* Manuscript Metadata in Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '9pt', color: '#000', lineHeight: '1.3' }}>
                      <div>
                        <strong>Book Title:</strong> {report.book_title || 'N/A'}
                      </div>
                      <div>
                        <strong>Author:</strong> {report.author_name || 'N/A'}
                      </div>
                      <div>
                        <strong>Subtitle:</strong> {report.subtitle || 'N/A'}
                      </div>
                      <div>
                        <strong>Publisher:</strong> {report.publisher_name || 'N/A'}
                      </div>
                      <div>
                        <strong>Sales Price:</strong> ${report.book_price || 'N/A'}
                      </div>
                      <div>
                        <strong>Release Date:</strong> {report.release_date || 'N/A'}
                      </div>
                      <div style={{ gridColumn: 'span 2' }}>
                        <strong>ISBN:</strong> {report.isbn || 'Not provided'}
                      </div>
                    </div>
                  </div>

                  {/* Print Content - Same structure as visible content but print-optimized */}
                  <div style={{ fontSize: '11pt', lineHeight: '1.5', color: '#1e293b' }}>
                    {/* Content rendering logic - duplicated from visible section */}
                    {viewingReport === 'marketing_analysis' && viewingReportContent && (
                      <div>
                        {viewingReportContent.book_overview && (
                          <div className="print-section">
                            <h3>Book Overview</h3>
                            <p>{viewingReportContent.book_overview}</p>
                          </div>
                        )}
                        {viewingReportContent.narrative_hook && (
                          <div className="print-section">
                            <h3>Narrative Hook</h3>
                            <p style={{ fontStyle: 'italic' }}>{viewingReportContent.narrative_hook}</p>
                          </div>
                        )}
                        {viewingReportContent.market_positioning && (
                          <div className="print-section">
                            <h3>Market Positioning</h3>
                            <p>{viewingReportContent.market_positioning}</p>
                          </div>
                        )}
                        {viewingReportContent.target_audience && (
                          <div className="print-section">
                            <h3>Target Audience</h3>
                            <p>{viewingReportContent.target_audience}</p>
                          </div>
                        )}
                        {viewingReportContent.why_this_will_sell && (
                          <div className="print-section">
                            <h3>Why This Will Sell</h3>
                            <p>{viewingReportContent.why_this_will_sell}</p>
                          </div>
                        )}
                        {viewingReportContent.author_bio_summary && (
                          <div className="print-section">
                            <h3>Author Bio Summary</h3>
                            <p>{viewingReportContent.author_bio_summary}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReport === 'press_release' && viewingReportContent.title && (
                      <div>
                        <h2>{viewingReportContent.title}</h2>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{viewingReportContent.content}</div>
                      </div>
                    )}

                    {viewingReport === 'author_bio' && (
                      <div>
                        {viewingReportContent.short_bio && (
                          <div className="print-section">
                            <h3>Short Bio (75-100 words)</h3>
                            <p>{viewingReportContent.short_bio}</p>
                          </div>
                        )}
                        {viewingReportContent.long_bio && (
                          <div className="print-section">
                            <h3>Long Bio (200-250 words)</h3>
                            <p>{viewingReportContent.long_bio}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReport === 'one_sheet' && (
                      <div>
                        {viewingReportContent.quick_facts && (
                          <div className="print-section">
                            <h3>Quick Facts</h3>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{viewingReportContent.quick_facts}</p>
                          </div>
                        )}
                        {viewingReportContent.hook && (
                          <div className="print-section">
                            <h3>Hook</h3>
                            <p>{viewingReportContent.hook}</p>
                          </div>
                        )}
                        {viewingReportContent.synopsis && (
                          <div className="print-section">
                            <h3>Synopsis</h3>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{viewingReportContent.synopsis}</p>
                          </div>
                        )}
                        {viewingReportContent.key_themes && (
                          <div className="print-section">
                            <h3>Key Themes</h3>
                            {Array.isArray(viewingReportContent.key_themes) ? (
                              <ul>
                                {viewingReportContent.key_themes.map((theme, i) => (
                                  <li key={i}>{theme}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>{viewingReportContent.key_themes}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.target_audience && (
                          <div className="print-section">
                            <h3>Target Audience</h3>
                            {Array.isArray(viewingReportContent.target_audience) ? (
                              <ul>
                                {viewingReportContent.target_audience.map((audience, i) => (
                                  <li key={i}>{audience}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>{viewingReportContent.target_audience}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.author_credentials && (
                          <div className="print-section">
                            <h3>Author Credentials</h3>
                            {Array.isArray(viewingReportContent.author_credentials) ? (
                              <ul>
                                {viewingReportContent.author_credentials.map((cred, i) => (
                                  <li key={i}>{cred}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>{viewingReportContent.author_credentials}</p>
                            )}
                          </div>
                        )}
                        {viewingReportContent.comparable_titles && (
                          <div className="print-section">
                            <h3>Comparable Titles</h3>
                            {Array.isArray(viewingReportContent.comparable_titles) ? (
                              <div>
                                {viewingReportContent.comparable_titles.map((comp, i) => (
                                  <div key={i} style={{ marginBottom: '10px' }}>
                                    <p style={{ fontWeight: 'bold' }}>{comp.title} by {comp.author}</p>
                                    <p style={{ fontSize: '10pt', marginTop: '3px' }}>{comp.why}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p>{viewingReportContent.comparable_titles}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReport === 'interview_questions' && (
                      <div>
                        {viewingReportContent.warm_up && (
                          <div className="print-section">
                            <h3>Warm-Up Questions</h3>
                            <ol>
                              {viewingReportContent.warm_up.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {viewingReportContent.deep_dive && (
                          <div className="print-section">
                            <h3>Deep-Dive Questions</h3>
                            <ol>
                              {viewingReportContent.deep_dive.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {viewingReportContent.engagement && (
                          <div className="print-section">
                            <h3>Audience Engagement</h3>
                            <ol>
                              {viewingReportContent.engagement.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {viewingReportContent.promotional && (
                          <div className="print-section">
                            <h3>Promotional Hooks</h3>
                            <ol>
                              {viewingReportContent.promotional.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReport === 'book_description_short' && (
                      <div className="print-section">
                        <p>{viewingReportContent.short_description}</p>
                      </div>
                    )}

                    {viewingReport === 'book_description_long' && (
                      <div className="print-section">
                        <p style={{ whiteSpace: 'pre-wrap' }}>{viewingReportContent.long_description}</p>
                      </div>
                    )}

                    {viewingReport === 'social_media_posts' && (
                      <div>
                        {viewingReportContent.twitter && (
                          <div className="print-section">
                            <h3>Twitter/X Posts</h3>
                            {viewingReportContent.twitter.map((post, i) => (
                              <div key={i} style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: '3px solid #ddd' }}>
                                <p>{post}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {viewingReportContent.instagram && (
                          <div className="print-section">
                            <h3>Instagram Posts</h3>
                            {viewingReportContent.instagram.map((post, i) => (
                              <div key={i} style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: '3px solid #ddd' }}>
                                <p>{post}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {viewingReportContent.facebook && (
                          <div className="print-section">
                            <h3>Facebook Posts</h3>
                            {viewingReportContent.facebook.map((post, i) => (
                              <div key={i} style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: '3px solid #ddd' }}>
                                <p>{post}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {viewingReportContent.linkedin && (
                          <div className="print-section">
                            <h3>LinkedIn Posts</h3>
                            {viewingReportContent.linkedin.map((post, i) => (
                              <div key={i} style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: '3px solid #ddd' }}>
                                <p>{post}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {viewingReport === 'talking_points' && (
                      <div>
                        {Object.entries(viewingReportContent).map(([key, value], idx) => (
                          <div key={idx} className="print-section">
                            <h3 style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</h3>
                            <p>{value}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {viewingReport === 'review_quotes' && viewingReportContent.quotes && (
                      <div>
                        {viewingReportContent.quotes.map((quote, i) => (
                          <div key={i} className="print-section">
                            <p style={{ fontStyle: 'italic', marginBottom: '5px' }}>"{quote.text}"</p>
                            <p style={{ fontSize: '10pt', color: '#64748b' }}>{quote.attribution}</p>
                            {quote.rating && (
                              <p style={{ fontSize: '10pt', fontWeight: 'bold' }}>{quote.rating}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Print Footer */}
                  <div className="print-footer">
                    Generated with Digital Ink AI - www.DigitalInkAI.com
                  </div>
                </div>
              )}
              
              {/* Marketing Analysis - Show only when no other report is being viewed OR when specifically viewing marketing_analysis */}
              {report.summary?.marketing_analysis && (viewingReport === null || viewingReport === 'marketing_analysis') && (
                <Card id="marketing-analysis-report" className="border-l-4 border-green-500 bg-gradient-to-br from-green-50 to-white">
                  <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">📊</span>
                      <div>
                        <CardTitle className="text-2xl">Marketing Analysis</CardTitle>
                        <p className="text-green-100 text-sm mt-1">Strategic insights demonstrating our deterministic approach</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    
                    {/* Book Overview */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <span className="text-blue-600">📖</span> Book Overview
                      </h3>
                      <p className="text-slate-700 leading-relaxed">
                        {report.summary.marketing_analysis.book_overview}
                      </p>
                    </div>

                    {/* Narrative Hook - Highlighted */}
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r">
                      <h3 className="text-lg font-bold text-orange-900 mb-2 flex items-center gap-2">
                        <span>🎯</span> Narrative Hook
                      </h3>
                      <p className="text-orange-900 leading-relaxed italic font-medium">
                        {report.summary.marketing_analysis.narrative_hook}
                      </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid md:grid-cols-2 gap-6">
                      
                      {/* Market Positioning */}
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                          <span className="text-purple-600">📍</span> Market Positioning
                        </h3>
                        <p className="text-slate-700 leading-relaxed">
                          {report.summary.marketing_analysis.market_positioning}
                        </p>
                      </div>

                      {/* Target Audience */}
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                          <span className="text-pink-600">👥</span> Target Audience
                        </h3>
                        <p className="text-slate-700 leading-relaxed">
                          {report.summary.marketing_analysis.target_audience}
                        </p>
                      </div>

                    </div>

                    {/* Why This Will Sell - Highlighted */}
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
                      <h3 className="text-lg font-bold text-green-900 mb-2 flex items-center gap-2">
                        <span>💰</span> Why This Will Sell
                      </h3>
                      <p className="text-green-900 leading-relaxed">
                        {report.summary.marketing_analysis.why_this_will_sell}
                      </p>
                    </div>

                    {/* Author Bio Summary */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <span className="text-indigo-600">✍️</span> Author Bio Summary
                      </h3>
                      <p className="text-slate-700 leading-relaxed">
                        {report.summary.marketing_analysis.author_bio_summary}
                      </p>
                    </div>

                  </CardContent>
                  <div className="px-6 pb-6 flex gap-3 print:hidden">
                    <button
                      onClick={handlePrintReport}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      🖨️ Print Report
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700"
                    >
                      📄 Export PDF
                    </button>
                  </div>
                </Card>
              )}
              {/* Sections moved above - duplicates removed */}
            </div>
            </div>
            </main>
          </div>
        ) : (
        // Form Display - When No Report
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle>Create New Media Kit</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">Please use the Dashboard to create a new media kit.</p>
              <Button onClick={() => navigate('/user_dashboard')} className="mt-4">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

        {/* Payment Validation Modal */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={() => {
          setShowPaymentValidation(false);
          navigate('/user_dashboard');
        }}
        onSuccess={handlePaymentValidationSuccess}
      />

      {/* Service Charge Authorization Modal */}
      <ServiceChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        onConfirm={handleServiceChargeAuthorization}
        serviceInfo={chargeInfo}
        userType={user?.role || 'a_la_carte'}
      />

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="Manuscript Media Kit Generator (MMKG)"
        bookTitle={formData.book_title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={() => setShowEmailReceiptModal(false)}
        onSendReceipt={async () => {
          try {
            await axios.post(`${API}/send-receipt-email`, {
              transaction_id: lastTransactionId,
              user_email: user.email
            }, { withCredentials: true });
            toast.success('Receipt sent to your email!');
            setShowEmailReceiptModal(false);
          } catch (error) {
            toast.error('Failed to send receipt');
          }
        }}
        serviceName="Manuscript Media Kit Generator (MMKG)"
        amount={lastCharge}
        bookTitle={formData.book_title}
      />

      {/* Service Access Denied */}
      {serviceAccessDenied && !showPaymentValidation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Service Access Required</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Please complete payment validation to access this service.
              </p>
              <Button 
                onClick={() => navigate('/user_dashboard')}
                className="w-full"
              >
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Show form only if payment completed */}
      {!paymentCompleted && !showPaymentValidation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="animate-pulse">
                  <div className="w-12 h-12 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Validating Service Access</h3>
                <p className="text-slate-600 mb-4">Please wait while we verify your payment information...</p>
                <Button 
                  onClick={validateServiceAccess}
                  variant="outline"
                  className="mt-4"
                >
                  Retry Validation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </>
  );
};

export default MMKGPage;
